import React, { useEffect, useMemo } from 'react';
import { useStore, useStoreActions } from "../../../contexts";
import GraphWidgetBottomCard from "../GraphWidget/GraphWidgetBottomCard";
import GraphWidgetLayout from "../GraphWidget/GraphWidgetLayout";
import MonthlyEarningsTopCard from "./MonthlyEarningsTopCard/MonthlyEarningsTopCard";
import MonthlyEarningsChart from "./MonthlyEarningsChart";
import { getTotal, chartDataForLoading, fillMissingDays } from "./MonthlyEarningsWidget.utils";
import { Callout, Typography } from 'seagull';
import VendorDashboardSeeWhatItsLike from "../VendorDashboardSeeWhatItsLike";
import styles from "../VendorDashboard.module.scss";
export default function MonthlyEarningsWidget() {
  const {
    fetchMonthlyEarnings
  } = useStoreActions();
  const {
    data,
    loading,
    error
  } = useStore(state => state.monthlyEarnings);
  useEffect(() => {
    fetchMonthlyEarnings();
  }, []);
  const title = useMemo(() => {
    const month = new Date().toLocaleString('default', {
      month: 'long'
    });
    const year = new Date().toLocaleString('default', {
      year: 'numeric'
    });
    return "Expected earnings for ".concat(month, " ").concat(year, " - only for issued invoices");
  }, []);
  const totals = useMemo(() => {
    if (!data) {
      return {};
    }
    return {
      paid: getTotal(data.totals.paid),
      in_transit: getTotal(data.totals.paymentConfirmed),
      clear: getTotal(data.totals.processingCharge),
      future: getTotal(data.totals.pending)
    };
  }, [data]);
  const chartData = useMemo(() => {
    if (!data) {
      return null;
    }
    const now = new Date();
    const monthString = (now.getMonth() + 1).toString().padStart(2, '0');
    return fillMissingDays(data.calendar).map(item => {
      const isToday = item.date.day === now.getDate();
      return {
        type: item.date.day,
        date: isToday ? 'Today' : "".concat(monthString, "/").concat(item.date.day.toString().padStart(2, '0')),
        paid: getTotal(item.statuses.paid),
        in_transit: getTotal(item.statuses.paymentConfirmed),
        clear: getTotal(item.statuses.processingCharge),
        future: getTotal(item.statuses.pending)
      };
    });
  }, [data]);
  return /*#__PURE__*/React.createElement(GraphWidgetLayout, {
    title: title,
    loading: !!data && loading,
    subtitle: /*#__PURE__*/React.createElement(Callout, null, "Current payout times shown do not account for users with faster payments. A new widget reflecting this will be available soon.")
  }, /*#__PURE__*/React.createElement(MonthlyEarningsTopCard, {
    success: true,
    title: "In your bank account",
    amount: totals.paid,
    info: "The amount that was deposited in your bank account this month.",
    testid: "monthly-earnings-total-paid"
  }), /*#__PURE__*/React.createElement(MonthlyEarningsTopCard, {
    title: "Collected successfully",
    amount: totals.in_transit,
    info: "The total amount successfully collected from your clients. It will be automatically deposited in your bank account within 1\u20132 business days.",
    testid: "monthly-earnings-total-in-transit"
  }), /*#__PURE__*/React.createElement(MonthlyEarningsTopCard, {
    title: "ACH processing",
    amount: totals.clear,
    info: "The amount that is still awaiting ACH verification. The verification process may take up to 5 business days.",
    testid: "monthly-earnings-total-clear"
  }), /*#__PURE__*/React.createElement(MonthlyEarningsTopCard, {
    title: "Future payments",
    amount: totals.future,
    info: "The amount that is expected to be paid by the end of the month, based on issued invoices. This excludes Anchor\u2019s fees.",
    testid: "monthly-earnings-total-future"
  }), /*#__PURE__*/React.createElement(GraphWidgetBottomCard, {
    "data-testid": "monthly-earnings-chart",
    empty: !!data && !data.calendar.length,
    emptyMsg: /*#__PURE__*/React.createElement("div", {
      className: styles.emptyStateWithAction
    }, /*#__PURE__*/React.createElement(Typography, null, "Here you will see your expected monthly earnings"), /*#__PURE__*/React.createElement(VendorDashboardSeeWhatItsLike, {
      "data-testid": "monthly-earnings"
    })),
    loading: !chartData,
    loadingMsg: "Loading your expected monthly earnings",
    loadingOrEmptyBg: () => /*#__PURE__*/React.createElement(MonthlyEarningsChart, {
      chartData: chartDataForLoading
    }),
    error: error && !loading,
    onRefresh: fetchMonthlyEarnings
  }, chartData && /*#__PURE__*/React.createElement(MonthlyEarningsChart, {
    chartData: chartData
  })));
}